import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { SponsorBanner } from '../components/SponsorAd';
import {
  PostCard,
  CreatePostDialog,
  QuickPostBox,
  FeedSidebar,
  EmptyFeed
} from '../components/feed';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Feed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [userVotes, setUserVotes] = useState({});

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (postId, voteType, e) => {
    e.stopPropagation();
    try {
      const response = await axios.post(`${API}/posts/${postId}/vote`, { vote_type: voteType });
      const newVote = response.data.vote; // 'up', 'down', or null (removed)
      
      const oldVote = userVotes[postId];
      setUserVotes(prev => ({ ...prev, [postId]: newVote }));
      
      // Update post score in UI based on what actually changed
      setPosts(posts.map(p => {
        if (p.id !== postId) return p;
        
        let scoreChange = 0;
        let upChange = 0;
        let downChange = 0;
        
        // Calculate based on the transition from oldVote to newVote
        if (!oldVote && newVote === 'up') {
          // No vote -> upvote
          scoreChange = 1; upChange = 1;
        } else if (!oldVote && newVote === 'down') {
          // No vote -> downvote
          scoreChange = -1; downChange = 1;
        } else if (oldVote === 'up' && !newVote) {
          // Upvote -> removed
          scoreChange = -1; upChange = -1;
        } else if (oldVote === 'down' && !newVote) {
          // Downvote -> removed
          scoreChange = 1; downChange = -1;
        } else if (oldVote === 'up' && newVote === 'down') {
          // Upvote -> downvote
          scoreChange = -2; upChange = -1; downChange = 1;
        } else if (oldVote === 'down' && newVote === 'up') {
          // Downvote -> upvote
          scoreChange = 2; upChange = 1; downChange = -1;
        }
        // If oldVote === newVote, no change needed (shouldn't happen)
        
        return {
          ...p,
          score: (p.score || 0) + scoreChange,
          upvotes: (p.upvotes || 0) + upChange,
          downvotes: (p.downvotes || 0) + downChange
        };
      }));
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleLike = async (postId) => {
    try {
      if (likedPosts.has(postId)) {
        await axios.delete(`${API}/posts/${postId}/like`);
        setLikedPosts(prev => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });
        setPosts(posts.map(p => p.id === postId ? { ...p, likes_count: p.likes_count - 1 } : p));
      } else {
        await axios.post(`${API}/posts/${postId}/like`);
        setLikedPosts(prev => new Set([...prev, postId]));
        setPosts(posts.map(p => p.id === postId ? { ...p, likes_count: p.likes_count + 1 } : p));
      }
    } catch (error) {
      if (error.response?.status !== 400) {
        toast.error('Failed to update like');
      }
    }
  };

  const handleBlock = (authorId) => {
    setPosts(posts.filter(p => p.author_id !== authorId));
  };

  const handlePostCreated = (newPost) => {
    setPosts([newPost, ...posts]);
  };

  // Check if user should see ads
  const showAds = !user || user.ad_preference !== 'none';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Sponsor Banner - Top */}
            {showAds && (
              <div className="mb-6">
                <SponsorBanner 
                  interests={user?.interests || []} 
                  page="feed" 
                  size="medium"
                />
              </div>
            )}
            
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-heading font-medium">Your Feed</h1>
                <p className="text-muted-foreground">What's happening in your dog community</p>
              </div>
              <CreatePostDialog 
                open={createOpen} 
                onOpenChange={setCreateOpen}
                onPostCreated={handlePostCreated}
              />
            </div>

            {/* Quick Post Box */}
            <QuickPostBox user={user} onCreateClick={() => setCreateOpen(true)} />

            {/* Posts List */}
            {posts.length === 0 ? (
              <EmptyFeed onCreateClick={() => setCreateOpen(true)} />
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    user={user}
                    userVote={userVotes[post.id]}
                    isLiked={likedPosts.has(post.id)}
                    onVote={handleVote}
                    onLike={handleLike}
                    onBlock={handleBlock}
                    onRefresh={fetchPosts}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <FeedSidebar user={user} showAds={showAds} />
        </div>
      </div>
    </div>
  );
};

export default Feed;
